import Link from "next/link"
import { LocalizedMatchEventWithMessage } from "../../../../apis/MatchAPI"
import moment from "moment"
import "moment/locale/it"
import {
  getCompetitionPageUrl,
  getCompetitionSeasonGroupPageUrl,
  getCompetitionSeasonGroupTurnPageUrl,
  getCompetitionSeasonPageUrl,
  getPlayerPageUrl,
  getTeamPageUrl,
} from "../../../../utils/urls"
import { LocalizedMatch } from "../../../../types/match"
import * as ImageConstants from "../../../../constants/images"
import { useEffect } from "react"
import * as LanguageConstants from "../../../../constants/languages"
import LastFiveMatchResult from "@components/Match/LastFiveMatchResult"
import Image from "next/image"
import style from "./style.module.scss"
import { Row, Col } from "react-bootstrap"
import { NewTitleBanner } from "@components/Banners/NewTitleBanner"
import cx from "classnames"
import PredictionQuotes from "@components/Predictions/PredictionQuotes"
import { SimplifiedNews } from "types/news"

const importantEventsType = {
  goal: true,
  "own-goal": true,
  "card yellow": false,
  "card red": false,
  "card yellow-red": false,
}

export function MatchScoreboard({
  match,
  events,
  locale,
  homeTeamLastMatchesPlayed,
  awayTeamLastMatchesPlayed,
  prediction,
}: {
  match: LocalizedMatch
  events: LocalizedMatchEventWithMessage[]
  locale?: string
  homeTeamLastMatchesPlayed: LocalizedMatch[]
  awayTeamLastMatchesPlayed: LocalizedMatch[]
  prediction?: SimplifiedNews | null
}) {
  useEffect(() => {
    moment.locale(locale || LanguageConstants.DEFAULT_LOCALE)
  }, [locale])

  const teamHomeUrl = getTeamPageUrl(
    match.homeTeam!.calciocomSlug!,
    match.competition?.calciocomSlug,
    match.season?.calciocomSlug,
  )
  const teamAwayUrl = getTeamPageUrl(
    match.awayTeam!.calciocomSlug!,
    match.competition?.calciocomSlug,
    match.season?.calciocomSlug,
  )
  let competitionUrl = ""

  if (match.competition) {
    competitionUrl = getCompetitionPageUrl(match.competition.calciocomSlug!)
    if (match.season) {
      competitionUrl = getCompetitionSeasonPageUrl(match.competition.calciocomSlug!, match.season.calciocomSlug!)
      if (match.group) {
        competitionUrl = getCompetitionSeasonGroupPageUrl(
          match.competition.calciocomSlug!,
          match.season.calciocomSlug!,
          match.group.calciocomSlug!,
        )
        if (match.turn) {
          competitionUrl = getCompetitionSeasonGroupTurnPageUrl(
            match.competition.calciocomSlug!,
            match.season.calciocomSlug!,
            match.group.calciocomSlug!,
            match.turn.calciocomSlug!,
          )
        }
      }
    }
  }
  const matchIsLive =
    match.status != null && match.ended === false && match.status != "postponed" && match.status != "abandoned"

  return (
    <div className={style.matchScoreboardMainContainer}>
      <NewTitleBanner title={`${match.homeTeam?.name} - ${match.awayTeam?.name}`} isH1 isLive={matchIsLive} />
      <Row className={style.matchDate}>
        <Col md={12} lg={12} xl={12}>
          <span>
            <strong>
              <Link href={competitionUrl}>
                <a title={match?.competition?.name}>{match?.competition?.name}</a>
              </Link>
            </strong>
            {" - "}
          </span>
          <span>
            <strong>{match.turn?.name}</strong>
          </span>
        </Col>
        <Col lg={12}>
          {match.ended ? (
            match.awayScoreP && match.homeScoreP ? (
              <span>Finita dopo i rigori</span>
            ) : (
              <span>Finita</span>
            )
          ) : matchIsLive ? (
            <span>In corso</span>
          ) : (
            <span>{moment(match.date).format("DD.MM.YY - HH.mm")} </span>
          )}
        </Col>
        <Col lg={12}>
          <span>{matchIsLive ? <span className={style.matchMinute}>{match.currentMinute}°</span> : null}</span>
        </Col>
      </Row>
      <Row className={style.matchInfo}>
        <Col xs={4} sm={4} md={5} lg={5} xl={5}>
          <Row className={style.homeTeam}>
            <Col className={cx(style.homeTeamImg, style.mobileOnly)} md={4} lg={4} xl={4}>
              <Link href={teamHomeUrl}>
                <a title={match.homeTeam?.name}>
                  <Image
                    src={match.homeTeam?.image?.path || ImageConstants.TEAM_MISSING_IMAGE_PATH}
                    alt={match.homeTeam?.name}
                    title={match.homeTeam?.name}
                    layout={"intrinsic"}
                    width={100}
                    height={100}
                  />
                </a>
              </Link>
            </Col>
            <Col className={style.homeTeamInfo} md={8} lg={8} xl={8}>
              <Row className={style.homeTeamName}>{match.homeTeam?.name}</Row>
              <Row className={style.homeTeamLastMatches}>
                <LastFiveMatchResult matches={homeTeamLastMatchesPlayed} teamId={match.homeTeamId!} home={true} />
              </Row>
            </Col>
            <Col className={style.homeTeamImg} md={4} lg={4} xl={4}>
              <Link href={teamHomeUrl}>
                <a title={match.homeTeam?.name}>
                  <Image
                    src={match.homeTeam?.image?.path || ImageConstants.TEAM_MISSING_IMAGE_PATH}
                    alt={match.homeTeam?.name}
                    title={match.homeTeam?.name}
                    layout={"intrinsic"}
                    width={100}
                    height={100}
                  />
                </a>
              </Link>
            </Col>
          </Row>
        </Col>
        <Col xs={4} sm={4} md={2} lg={2} xl={2} className={style.matchScoreContainer}>
          <Row className={style.resultContainer}>
            {match.result ? (
              <Col xs={5} sm={5} md={5} lg={5} xl={5}>
                <span className={style.result}>
                  <strong>{match.homeScore}</strong>
                </span>
                {match.homeScoreP != null ? (
                  <span className={style.penalty}>
                    <strong>({match.homeScoreP})</strong>
                  </span>
                ) : null}
              </Col>
            ) : null}
            <Col xs={2} sm={2} md={2} lg={2} xl={2}>
              {match.result ? <span className={style.vs}>-</span> : <span className={style.vs}>VS</span>}
              {match.homeScoreP != null ? (
                <span className={style.vs} style={{ color: "transparent" }}>
                  -
                </span>
              ) : (
                <></>
              )}
            </Col>
            {match.result ? (
              <Col xs={5} sm={5} md={5} lg={5} xl={5}>
                <span className={style.result}>
                  <strong>{match.awayScore}</strong>
                </span>
                {match.homeScoreP != null ? (
                  <span className={style.penalty}>
                    <strong>({match.awayScoreP})</strong>
                  </span>
                ) : null}
              </Col>
            ) : null}
          </Row>
        </Col>
        <Col xs={4} sm={4} md={5} lg={5} xl={5}>
          <Row className={style.awayTeam}>
            <Col className={style.awayTeamImg} md={4} lg={4} xl={4}>
              <Link href={teamAwayUrl}>
                <a title={match.awayTeam?.name}>
                  <Image
                    src={match.awayTeam?.image?.path || ImageConstants.TEAM_MISSING_IMAGE_PATH}
                    alt={match.awayTeam?.name}
                    title={match.awayTeam?.name}
                    layout={"intrinsic"}
                    width={100}
                    height={100}
                  />
                </a>
              </Link>
            </Col>
            <Col className={style.awayTeamInfo} md={8} lg={8} xl={8}>
              <Row className={style.awayTeamName}>{match.awayTeam?.name}</Row>
              <Row className={style.awayTeamLastMatches}>
                <LastFiveMatchResult matches={awayTeamLastMatchesPlayed} teamId={match.awayTeamId!} home={false} />
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <Col xs={6} sm={6} md={3} lg={3} xl={3} className={style.matchBet}>
          {events
            .filter((event) => event.team && event.team.id === match.homeTeamId)
            .map((event, index) => {
              const isImportant = importantEventsType[event.type as keyof typeof importantEventsType]
              if (!isImportant || (!event.player && !event.playerName)) {
                return null
              }
              const playerUrl = event.player && getPlayerPageUrl(event.player.calciocomSlug!)

              return (
                <Row key={index} className={style.event}>
                  <Col xs={2} sm={2} md={2} lg={2} xl={2} className={style.eventImg}>
                    <Image src={`/event_icons/${event.type}.png`} width={15} height={15} alt="event" />
                  </Col>
                  <Col xs={10} sm={10} md={10} lg={10} xl={10}>
                    {event.player && playerUrl ? (
                      <Link href={playerUrl}>
                        <a title={event.player.name}>
                          {event.player.name} ({event.minute}&apos;)
                        </a>
                      </Link>
                    ) : (
                      <>
                        {event.playerName} ({event.minute}&apos;)
                      </>
                    )}
                  </Col>
                </Row>
              )
            })}
        </Col>
        <Col md={6} lg={6} xl={6} className={style.matchBetWidget}>
          {!match.ended && prediction ? <PredictionQuotes col={12} news={prediction} /> : null}
        </Col>
        <Col xs={6} sm={6} md={3} lg={3} xl={3} className={style.matchBet}>
          {events
            .filter((event) => event.team && event.team.id === match.awayTeamId)
            .map((event, index) => {
              const isImportant = importantEventsType[event.type as keyof typeof importantEventsType]
              if (!isImportant || (!event.player && !event.playerName)) {
                return null
              }
              const playerUrl = event.player && getPlayerPageUrl(event.player.calciocomSlug!)

              return (
                <Row key={index} className={style.event}>
                  <Col xs={2} sm={2} md={2} lg={2} xl={2}>
                    <Image src={`/event_icons/${event.type}.png`} width={15} height={15} alt="event" />
                  </Col>
                  <Col xs={10} sm={10} md={10} lg={10} xl={10}>
                    {event.player && playerUrl ? (
                      <Link href={playerUrl}>
                        <a title={event.player.name}>
                          {event.player.name} ({event.minute}&apos;)
                        </a>
                      </Link>
                    ) : (
                      <>
                        {event.playerName} ({event.minute}&apos;)
                      </>
                    )}
                  </Col>
                </Row>
              )
            })}
        </Col>
      </Row>
      <Row>
        {!match.ended && prediction ? (
          <Col className={style.matchBetWidgetMobile}>
            <PredictionQuotes col={10} news={prediction} />
          </Col>
        ) : null}
      </Row>
    </div>
  )
}
